import React from 'react'
import { connect } from 'react-redux'
import { Menu, Layout, Spin } from 'antd'
import classNames from 'classnames'
import store from 'store'
import { find } from 'lodash'
import { withRouter } from 'router'
import SimpleBar from 'simplebar-react'
import SVG from 'react-inlinesvg'

import ResourceService from 'api/Resource'
import GroupsService from 'api/Groups'
import GroupService from 'api/Group'
import i18n from 'i18n'

import { confirmBase } from 'utils/ui'
import { setTempEnvParam, removeTempEnvParam } from 'env'
import orgChartHelper from 'pages/records/components/OrgChartTree/helper'
import cheveronRight24SVG from '@/assets/images/cheveron-right-24.svg'
import cheveronDown24SVG from '@/assets/images/cheveron-down-24.svg'
import infoIconSVG from '@/assets/images/icon-info-16.svg'
import GroupMemberPopup from './GroupMemberPopup'
import ResourceQuotaCard from './ResourceQuotaCard'
import styles from './style.module.scss'

const mapStateToProps = ({ menu, settings, user, dispatch }) => ({
  menuData: menu.menuData,
  adminMenuData: menu.adminMenuData,
  isMenuCollapsed: settings.isMenuCollapsed,
  isSidebarOpen: settings.isSidebarOpen,
  isMobileView: settings.isMobileView,
  isMenuUnfixed: settings.isMenuUnfixed,
  isMenuShadow: settings.isMenuShadow,
  menuColor: settings.menuColor,
  // logo: settings.logo,
  role: user.role,
  workspaceId: user.workspace?.id,
  dispatch,
  settings,
})

class RecordMenu extends React.Component {
  state = {
    selectedKeys: store.get('app.menu.selectedKeys') || ['dashboardnine'],
    openedKeys: store.get('app.menu.openedKeys') || ['email', 'group1', 'category1'],
    isMobile: window.innerWidth <= 1029,
  }

  menuInfoMap = {}

  menuKeyMap = []

  groupInfo = {
    countMap: {},
    menuItems: [],
  }

  categoryInfo = {
    countMap: {},
    menuItems: [],
  }

  componentDidMount() {
    this.updateComponent()
  }

  componentDidUpdate(prevProps /* , prevState */) {
    const { location, menuData, adminMenuData, menuType, workspaceId } = this.props

    // 경로 변경 시 메뉴 선택 상태만 갱신
    if (prevProps.location.pathname !== location.pathname) {
      this.applySelectedKeys()
    }

    // const isWorkspaceChanged = prevProps.workspaceId !== workspaceId
    const isUserMenuTypeChanged =
      prevProps.menuType === 'user' &&
      prevProps.menuData !== menuData &&
      prevProps.workspaceId === workspaceId
    const isAdminMenuTypeChanged =
      prevProps.menuType === 'admin' &&
      prevProps.adminMenuData !== adminMenuData &&
      prevProps.workspaceId === workspaceId
    const isMenuTypeChanged =
      prevProps.menuType !== menuType && prevProps.workspaceId === workspaceId

    // 메뉴 데이터/타입 변경 시 갱신 (workspace 동일한 경우)
    if (
      // isWorkspaceChanged ||
      isUserMenuTypeChanged ||
      isAdminMenuTypeChanged ||
      isMenuTypeChanged
    ) {
      this.updateComponent()
    }

    if (
      location.pathname.startsWith('/records/categories') ||
      location.pathname.startsWith('/records/groups')
    ) {
      const filteredMenu = menuData.filter(
        (menu) => menu.key === 'categories' || menu.key === 'groups',
      )

      let pageTitle = null
      if (
        location.pathname.startsWith('/records/categories') ||
        location.pathname.startsWith('/records/groups')
      ) {
        const children = filteredMenu.flatMap((menu) => menu.children || [])
        const matched = children.find((child) => child.url === location.pathname)
        if (matched) {
          pageTitle = matched.title
        }

        setTempEnvParam('pageTitle', pageTitle)
      }
    } else {
      removeTempEnvParam('pageTitle')
    }
  }

  updateComponent = () => {
    this.loadRecordInfo()
    this.applySelectedKeys()
  }

  handleResize = () => {
    this.setState({ isMobile: window.innerWidth <= 1029 })
  }

  loadRecordInfo = () => {
    const { menuType } = this.props

    if (this.isLoadingRecordInfo) return
    if (menuType === 'user') {
      this.isLoadingRecordInfo = true

      const resourcePromise = ResourceService.info().then((res) => {
        if (res.data != null) {
          const {
            total_count: allRecords,
            favorite_count: favorites,
            my_resource_count: myRecords,
            expired_count: expiredRecords,
            totp_count: authenticator,
            memo_count: memoRecords,
            categories,
          } = res.data

          this.menuInfoMap = {
            ...this.menuInfoMap,
            allRecords,
            expiredRecords,
            favorites,
            myRecords,
            // 인증 코드 탭 배지(#1495) — 메뉴 key 'authenticator'와 매핑
            authenticator,
            // 메모 탭 배지 — 메뉴 key 'memoRecords'와 매핑
            memoRecords,
            categories,
          }

          this.categoryInfo = {
            countMap: {},
            menuItems: [],
          }

          const { countMap, menuItems } = this.categoryInfo

          categories?.forEach((category) => {
            countMap[category.id] = category.count
            menuItems.push({
              title: category.name,
              key: category.id,
              url: `/records/categories/${category.id}`,
            })
          })
        }
      })

      const groupPromise = GroupsService.get({ limit: 0 }).then((res) => {
        const { data } = res
        this.groupInfo = {
          countMap: {},
          menuItems: [],
        }

        if (data != null) {
          const { countMap, menuItems } = this.groupInfo

          data.forEach((group) => {
            countMap[group.id] = group.resource_count
            menuItems.push({
              title: group.name,
              key: group.id,
              url: `/records/groups/${group.id}`,
            })
          })
        }
      })

      Promise.all([resourcePromise, groupPromise]).then(() => {
        this.isLoadingRecordInfo = false
        this.forceUpdate()
        setTimeout(() => {
          this.applySelectedKeys()
        }, 100)
      })
    }
  }

  applySelectedKeys = () => {
    const {
      location: { pathname },
      menuData: userMenuData,
      adminMenuData,
      menuType,
    } = this.props

    const menuData = menuType === 'admin' ? adminMenuData : userMenuData

    const flattenItems = (items, key) =>
      items.reduce((flattenedItems, item) => {
        flattenedItems.push(item)
        if (Array.isArray(item[key])) {
          return flattenedItems.concat(flattenItems(item[key], key))
        }
        return flattenedItems
      }, [])

    // const selectedItem = find(flattenItems(menuData, 'children'), ['url', pathname])
    const menuItems = flattenItems(menuData, 'children')
    let selectedItem = find(menuItems, (menu) => {
      if (menu.url === pathname) {
        return true
      }
      return false
    })

    if (selectedItem == null) {
      let curMenu = null
      let mCnt = 0
      const pathArr = pathname.split('/')
      menuItems.forEach((menu) => {
        if (menu.url != null) {
          const urlArr = menu.url.split('/')
          const len = Math.min(urlArr.length, pathArr.length)
          // 0번째는 전부 ''이므로 비교할 필요없음.
          for (let i = 1; i < len; i += 1) {
            if (pathArr[i] === urlArr[i]) {
              if (mCnt < i + 1) {
                mCnt = i + 1
                curMenu = menu
              }
            } else {
              break
            }
          }
        }
      })

      if (curMenu != null) {
        selectedItem = curMenu
      }
    }

    const keys = selectedItem ? [selectedItem.key] : []
    store.set('app.menu.selectedKeys', keys)

    this.setState({
      selectedKeys: keys,
    })
  }

  onCollapse = (_, type) => {
    const { dispatch, isMenuCollapsed } = this.props

    if (type === 'responsive' && isMenuCollapsed) {
      return
    }
    dispatch({
      type: 'settings/CHANGE_SETTING',
      payload: {
        setting: 'isMenuCollapsed',
        value: !isMenuCollapsed,
      },
    })
    this.setState({
      openedKeys: [],
    })
  }

  onOpenChange = (keys) => {
    const { openedKeys } = this.state
    const filteredKeys = keys.filter((key) => key !== 'adminConsole')
    const currentOpenKeys = openedKeys || []
    const newKeys = currentOpenKeys.includes('adminConsole')
      ? [...filteredKeys, 'adminConsole']
      : filteredKeys

    store.set('app.menu.openedKeys', newKeys)
    this.setState({
      openedKeys: newKeys,
    })
  }

  handleClick = (e) => {
    const { dispatch, isSidebarOpen, isMobileView, navigate } = this.props

    if (e.key === 'settings') {
      dispatch({
        type: 'settings/CHANGE_SETTING',
        payload: {
          setting: 'isSidebarOpen',
          value: !isSidebarOpen,
        },
      })
      return
    }

    if (e.key !== 'adminConsole') {
      this.setState({
        selectedKeys: [e.key],
      })
    } else {
      this.handleAdminClick(e)
    }

    if (this.menuKeyMap[e.key] != null && e.key !== 'adminConsole') {
      if (this.menuKeyMap[e.key].url != null) {
        navigate(this.menuKeyMap[e.key].url)
      }
    }

    if (isMobileView === true) {
      dispatch({
        type: 'settings/CHANGE_SETTING',
        payload: {
          setting: 'isMobileMenuOpen',
          value: false,
        },
      })
    }
  }

  getCountElem = (menu) => {
    const { key } = menu
    const count =
      this.menuInfoMap[key] || this.groupInfo.countMap[key] || this.categoryInfo.countMap[key]

    if (count != null && count > 0) {
      const countStr = count > 999 ? '999+' : count.toString()
      const expiredStyle = key === 'expiredRecords' ? styles.expired : ''
      return <span className={classNames(styles.count, expiredStyle)}>{countStr}</span>
    }

    return null
  }

  handleInfoClick = (groupId, e) => {
    e.stopPropagation()
    e.preventDefault()

    const loadingModal = confirmBase({
      title: i18n.t('menu.groupMembers'),
      wrapClassName: styles.groupMemberModal,
      content: (
        <div
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 480 }}
        >
          <Spin />
        </div>
      ),
      width: 480,
      cancelButtonProps: { style: { display: 'none' } },
    })

    Promise.all([GroupService.get({ id: groupId }), orgChartHelper.loadData()])
      .then(([res, orgChartData]) => {
        const members = res.data || []
        loadingModal.update({
          content: <GroupMemberPopup members={members} orgChartData={orgChartData} />,
        })
      })
      .catch(() => {
        loadingModal.update({
          content: (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#ef4444' }}>
              {i18n.t('common.error.network', { defaultValue: 'Failed to load data' })}
            </div>
          ),
        })
      })
  }

  renderExpandIcon = ({ isOpen }) => {
    if (isOpen === true) {
      return <img src={cheveronDown24SVG} alt="opened" />
    }
    return <img src={cheveronRight24SVG} alt="closed" />
  }

  generateMenuItems = () => {
    const { menuData: userMenuData, adminMenuData, menuType, role } = this.props

    let menuData = menuType === 'admin' ? adminMenuData : userMenuData

    if (menuType === 'user') {
      menuData.forEach((menu) => {
        if (menu.key === 'groups') {
          menu.children = this.groupInfo.menuItems
          return true
        }

        if (menu.key === 'categories') {
          menu.children = this.categoryInfo.menuItems
          return true
        }

        return false
      })
    }

    menuData = menuData.filter((menu) => {
      if (menu.key === 'groups') {
        return menu.children && menu.children.length > 0 // children이 있으면 유지
      }

      if (menu.key === 'categories') {
        return menu.children && menu.children.length > 0 // children이 있으면 유지
      }

      return true // 다른 메뉴 항목은 그대로 유지
    })

    this.menuKeyMap = {}

    const generateItem = (item, parentKey) => {
      const { key, title, icon, disabled, count } = item
      const titleText = title
      const isGroupChild = parentKey === 'groups'

      if (item.key != null) {
        this.menuKeyMap[item.key] = item
      }

      if (item.divider === true) {
        return {
          type: 'divider',
        }
      }

      let iconElem = null

      if (icon == null || typeof icon === 'string') {
        iconElem = (
          <React.Fragment>
            {count && <span className="badge badge-success ml-2">{count}</span>}
            {icon && <span className={`${icon} ${styles.icon} icon-collapsed-hidden`} />}
          </React.Fragment>
        )
      } else {
        iconElem = (
          <React.Fragment>
            {count && <span className="badge badge-success ml-2">{count}</span>}
            <span className={`${styles.icon}`}>{icon}</span>
          </React.Fragment>
        )
      }

      const countElem = this.getCountElem(item)

      let adminWindow = null

      this.handleAdminClick = () => {
        if (adminWindow && !adminWindow.closed) {
          adminWindow.focus()
        } else {
          adminWindow = window.open('/admin/users', '_blank')
        }
      }

      return {
        label: isGroupChild ? (
          <div className={styles.groupItemWrapper}>
            <span className={styles.title} style={{ margin: '0' }}>
              {titleText}
            </span>
            <button
              type="button"
              className={styles.infoIcon}
              onClick={(e) => this.handleInfoClick(key, e)}
            >
              <img src={infoIconSVG} alt="info" />
            </button>
            {countElem}
          </div>
        ) : (
          <React.Fragment>
            <div className={styles.img}>
              {icon != null && <SVG src={icon} style={{ stroke: 'currentColor' }} />}
            </div>
            <span
              className={styles.title}
              style={{ margin: '0' }}
              onClick={key === 'adminConsole' ? this.handleAdminClick : undefined}
              onKeyDown={
                key === 'adminConsole'
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        this.handleAdminClick(e)
                      }
                    }
                  : undefined
              }
              tabIndex={key === 'adminConsole' ? 0 : undefined}
              role={key === 'adminConsole' ? 'button' : undefined}
            >
              {titleText}
            </span>

            {countElem}
          </React.Fragment>
        ),
        key,
        disabled,
        icon: iconElem,
      }
    }

    const generateSubmenu = (items, parentKey) =>
      items.map((menuItem) => {
        if (menuItem.children) {
          const titleText = menuItem.title

          let iconElem = null
          if (menuItem.icon == null || typeof menuItem.icon === 'string') {
            iconElem = (
              <React.Fragment>
                {menuItem.count && (
                  <span className="badge badge-success ml-2">{menuItem.count}</span>
                )}
                {menuItem.icon && <span className={`${menuItem.icon} ${styles.icon}`} />}
              </React.Fragment>
            )
          } else {
            iconElem = (
              <React.Fragment>
                {menuItem.count && (
                  <span className="badge badge-success ml-2">{menuItem.count}</span>
                )}
                <span className={`${styles.icon}`}>{menuItem.icon}</span>
              </React.Fragment>
            )
          }

          return {
            label: <span className={styles.title}>{titleText}</span>,
            key: menuItem.key,
            icon: iconElem,
            children: generateSubmenu(menuItem.children, menuItem.key),
          }
        }
        return generateItem(menuItem, parentKey)
      })

    return menuData.map((menuItem) => {
      if (menuItem.roles && !menuItem.roles.includes(role)) {
        return null
      }

      let iconElem = null
      if (menuItem.icon == null || typeof menuItem.icon === 'string') {
        iconElem = (
          <React.Fragment>
            {menuItem.count && (
              <React.Fragment>
                <span className="badge badge-success ml-2">{menuItem.count}</span>
              </React.Fragment>
            )}
            {menuItem.icon && <span className={`${menuItem.icon} ${styles.icon}`} />}
          </React.Fragment>
        )
      } else {
        iconElem = (
          <React.Fragment>
            {menuItem.count && <span className="badge badge-success ml-2">{menuItem.count}</span>}
            <span className={`${styles.icon}`}>{menuItem.icon}</span>
          </React.Fragment>
        )
      }
      if (menuItem.children) {
        const titleText = menuItem.title
        return {
          label: (
            <div className={styles.submenuContainer}>
              <div className={styles.submenu}>
                {menuItem.icon && (
                  <div className={styles.img}>
                    <SVG src={menuItem.icon} style={{ stroke: 'currentColor' }} />
                  </div>
                )}
                <span className={styles.title}>{titleText}</span>
              </div>
            </div>
          ),
          key: menuItem.key,
          icon: iconElem,
          children: generateSubmenu(menuItem.children, menuItem.key),
        }
      }

      return generateItem(menuItem)
    })
  }

  render() {
    const { selectedKeys, openedKeys, isMobile } = this.state
    const { menuColor, isMenuUnfixed, isMenuShadow, preElem, menuType } = this.props

    const menuSettings = isMobile
      ? {
          collapsible: false,
          collapsed: false,
          onCollapse: this.onCollapse,
          trigger: null,
          width: 240,
        }
      : {
          collapsible: true,
          collapsed: false, // isMenuCollapsed,
          onCollapse: this.onCollapse,
          breakpoint: 'lg',
          trigger: null,
          width: 240,
        }

    const menuItems = this.generateMenuItems()

    return (
      <Layout.Sider
        {...menuSettings}
        className={classNames(`${styles.menu}`, {
          [styles.white]: menuColor === 'white',
          [styles.gray]: menuColor === 'gray',
          [styles.dark]: menuColor === 'dark',
          [styles.unfixed]: isMenuUnfixed,
          [styles.shadow]: isMenuShadow,
        })}
      >
        <div className={styles.menuOuter}>
          {/* JP resource plan 개인 quota 카드 — 카드 내부에서 isJP/quota_mode 게이팅, 조건 미충족 시 null */}
          {menuType === 'user' && <ResourceQuotaCard />}
          {preElem && <div className={styles.preElem}>{preElem}</div>}
          <SimpleBar>
            <Menu
              onClick={this.handleClick}
              selectedKeys={selectedKeys}
              openKeys={openedKeys}
              onOpenChange={this.onOpenChange}
              mode="inline"
              className={styles.navigation}
              inlineIndent="15"
              items={menuItems}
              expandIcon={this.renderExpandIcon}
            />
          </SimpleBar>
        </div>
      </Layout.Sider>
    )
  }
}

export default connect(mapStateToProps)(withRouter(RecordMenu, { navigate: true }))
